/**
 * Drag-to-insert math.
 *
 * When an operator pulls the route line, the new via-point must land between
 * the two waypoints whose leg was grabbed. Picking the nearest *vertex* gets
 * this wrong on long straight runs and on routes that double back, so we
 * project onto every *segment* and then map the winning segment to its leg.
 */

export type Position = [number, number];

export interface SegmentHit {
  /** Index of the segment's first vertex in the line. */
  segmentIndex: number;
  /** 0..1 position along that segment. */
  t: number;
  /** The projected point itself. */
  point: Position;
  /** Approximate distance in metres from the query point. */
  distanceMeters: number;
}

const EARTH_RADIUS_M = 6371008.8;
const DEG_TO_M = (Math.PI / 180) * EARTH_RADIUS_M;

/**
 * Project lon/lat into a local equirectangular plane in metres. Over the span
 * of a single route segment this is accurate enough for hit-testing and keeps
 * the math cheap enough to run on every mousemove.
 */
function toPlane(p: Position, cosLat: number): [number, number] {
  return [p[0] * DEG_TO_M * cosLat, p[1] * DEG_TO_M];
}

export function projectOnSegment(point: Position, a: Position, b: Position): { t: number; point: Position; distanceMeters: number } {
  const cosLat = Math.cos((point[1] * Math.PI) / 180) || 1e-6;
  const [px, py] = toPlane(point, cosLat);
  const [ax, ay] = toPlane(a, cosLat);
  const [bx, by] = toPlane(b, cosLat);
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
  const projected: Position = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const [qx, qy] = toPlane(projected, cosLat);
  return {
    t,
    point: projected,
    distanceMeters: Math.hypot(px - qx, py - qy),
  };
}

/** Nearest point on a polyline, searched segment by segment. */
export function nearestPointOnLine(line: Position[], point: Position): SegmentHit | null {
  if (!line || line.length < 2) return null;
  let best: SegmentHit | null = null;
  for (let i = 0; i < line.length - 1; i += 1) {
    const hit = projectOnSegment(point, line[i], line[i + 1]);
    if (!best || hit.distanceMeters < best.distanceMeters) {
      best = { segmentIndex: i, t: hit.t, point: hit.point, distanceMeters: hit.distanceMeters };
    }
  }
  return best;
}

/**
 * Start vertex of each leg within the concatenated route geometry.
 * Returns `legs.length + 1` entries; the last is the final vertex index.
 * Consecutive legs share a vertex, hence the `- 1`.
 */
export function legVertexOffsetsFromSteps(
  legs: Array<{ steps?: Array<{ geometry?: { coordinates?: unknown[] } }> }>,
  totalVertices: number,
): number[] {
  const offsets: number[] = [0];
  let cursor = 0;
  for (const leg of legs) {
    const steps = leg.steps || [];
    if (steps.length === 0 || !steps.every((s) => Array.isArray(s.geometry?.coordinates))) {
      return evenOffsets(legs.length, totalVertices);
    }
    let legVertices = 1;
    for (const step of steps) {
      legVertices += (step.geometry!.coordinates!.length) - 1;
    }
    cursor += legVertices - 1;
    offsets.push(Math.min(cursor, Math.max(0, totalVertices - 1)));
  }
  return offsets;
}

/** Fallback when step geometries are missing: spread legs evenly. */
function evenOffsets(legCount: number, totalVertices: number): number[] {
  const last = Math.max(0, totalVertices - 1);
  const offsets: number[] = [];
  for (let i = 0; i <= legCount; i += 1) {
    offsets.push(Math.round((last * i) / Math.max(1, legCount)));
  }
  return offsets;
}

/** Which leg owns a given geometry vertex. */
export function legIndexForVertex(offsets: number[], vertexIndex: number): number {
  const legCount = Math.max(1, offsets.length - 1);
  for (let leg = legCount - 1; leg >= 0; leg -= 1) {
    if (vertexIndex >= offsets[leg]) return leg;
  }
  return 0;
}

/**
 * Waypoint index a dragged via-point should be inserted at.
 * A point grabbed on leg `n` (between waypoints n and n+1) inserts at n+1.
 */
export function insertionIndexForPoint(
  line: Position[],
  legOffsets: number[],
  point: Position,
): { index: number; hit: SegmentHit } | null {
  const hit = nearestPointOnLine(line, point);
  if (!hit) return null;
  // Leg boundaries always fall on vertices, so a segment belongs entirely to
  // the leg of its start vertex — never round toward the nearer endpoint.
  const legIndex = legIndexForVertex(legOffsets, hit.segmentIndex);
  const maxIndex = Math.max(1, legOffsets.length - 1);
  return { index: Math.min(legIndex + 1, maxIndex), hit };
}

/** Great-circle distance in metres. */
export function haversineMeters(a: Position, b: Position): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Point half-way along a polyline by distance — where a route badge is anchored. */
export function midpointOfLine(line: Position[]): Position | null {
  if (!line || line.length === 0) return null;
  if (line.length === 1) return line[0];
  let total = 0;
  const cumulative: number[] = [0];
  for (let i = 0; i < line.length - 1; i += 1) {
    total += haversineMeters(line[i], line[i + 1]);
    cumulative.push(total);
  }
  const half = total / 2;
  for (let i = 0; i < cumulative.length - 1; i += 1) {
    if (cumulative[i + 1] >= half) {
      const segmentLength = cumulative[i + 1] - cumulative[i] || 1;
      const t = (half - cumulative[i]) / segmentLength;
      return [
        line[i][0] + (line[i + 1][0] - line[i][0]) * t,
        line[i][1] + (line[i + 1][1] - line[i][1]) * t,
      ];
    }
  }
  return line[Math.floor(line.length / 2)];
}
